import styled from '@emotion/styled';
import { DARK_BLUE, GREEN } from '../colors';
import { ReactComponent as LogoCNC } from '../images/cnc-logo.svg';
import { ReactComponent as LogoJyros } from '../images/logo-jyros-white.svg';
import useReportCoverTexts from './useReportCoverTexts';

const CoverLayout = styled.div`
  background: ${DARK_BLUE};
  width: 210mm;
  height: 297mm;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  position: relative;
  padding: 0;
  overflow: hidden;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  width: 100%;
  padding: 2.5rem 2.5rem 0 2.5rem;
`;

export const DateText = styled.div`
  color: #fff;
  font-size: 1.6rem;
  line-height: 1.6rem;
  font-family: Causten, sans-serif;
  letter-spacing: 0.05em;
  border-right: 5px solid #fff;
  padding: 0.1em 0.2em 0.1em 0;
`;

const CenterBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8rem;
  margin-bottom: 8rem;
`;

export const MainTitle = styled.div`
  color: #fff;
  font-size: 3rem;
  font-family: Ibrand, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.2em;
`;

export const SubRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  width: 100%;
  justify-content: center;
`;

export const SubText = styled.div`
  color: ${GREEN};
  font-size: 1.5rem;
  font-family: Causten, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Divider = styled.div`
  width: 2px;
  height: 2.5rem;
  background: #fff;
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 4rem;
  align-items: center;
  width: 100%;
  padding: 0 2.5rem 0 2.5rem;
`;

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 20%;
  svg path,
  svg polygon {
    stroke-width: 0 !important;
    fill: #fff !important;
  }
`;

const JyrosSubtitle = styled.div`
  color: ${GREEN};
  font-size: 0.8rem;
  margin-left: -1rem;
  margin-right: -1rem;
  text-align: center;
  font-family: Ibrand, sans-serif;
`;

const CNCBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 20%;
  svg path,
  svg polygon {
    stroke-width: 0 !important;
    fill: #fff !important;
  }
`;

const CNCSubtitle = styled.div`
  color: ${GREEN};
  font-size: 1rem;
  margin-left: -2rem;
  margin-right: -2rem;
  text-align: center;
  font-family: Ibrand, sans-serif;
`;

const PartnersRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2.5rem;
  width: 100%;
  margin: 4rem auto 0rem auto;
`;

const PartnerLogo = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  > img {
    height: 100%;
  }
`;

const SimpleReportCover = ({
  reportDate,
  year,
  projectName,
}: {
  reportDate: Date;
  year: number | undefined;
  projectName?: string | undefined;
}) => {
  const { title, subtitle, companyText, jyrosSubtitle, homologationText } =
    useReportCoverTexts('simple', year);

  return (
    <CoverLayout>
      <TopRow>
        <DateText>
          {Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' }).format(
            reportDate,
          )}
        </DateText>
      </TopRow>
      <CenterBlock>
        <MainTitle>{title}</MainTitle>
        <SubRow>
          <SubText>{subtitle}</SubText>
          <SubText>{companyText}</SubText>
          {projectName && <SubText>{projectName}</SubText>}
        </SubRow>
      </CenterBlock>
      <div style={{ marginBottom: '4rem' }}>
        <BottomRow>
          <LogoBlock>
            <LogoJyros />
            <JyrosSubtitle>{jyrosSubtitle}</JyrosSubtitle>
          </LogoBlock>
          <Divider style={{ height: '5rem' }} />
          <CNCBlock>
            <CNCSubtitle>{homologationText}</CNCSubtitle>
            <LogoCNC />
          </CNCBlock>
        </BottomRow>
        <PartnersRow>
          <PartnerLogo>
            <img src="/Logo consortium.png" alt="Logo consortium" />
          </PartnerLogo>
          <PartnerLogo>
            <img src="/Logo ARA.png" alt="Logo région" />
          </PartnerLogo>
          <PartnerLogo>
            <img
              src="/Logo caisse des dépots.png"
              alt="Logo caisse des dépots"
            />
          </PartnerLogo>
          <PartnerLogo>
            <img src="/France 2030.png" alt="Logo France 2030" />
          </PartnerLogo>
        </PartnersRow>
      </div>
    </CoverLayout>
  );
};

export default SimpleReportCover;
